/**
 * Диалог пометки на снимке (ПР-03): палец/мышь рисуют красные штрихи, «Сохранить»
 * впекает их в JPEG. Только для фото, ещё не ушедших на сервер.
 */
import { useEffect, useRef, useState } from 'react';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { bakeMarkupOntoPhoto, type MarkupPoint, type MarkupStroke } from './offline/photo-markup';

export function PhotoMarkupDialog({
  blob,
  onClose,
  onSave,
}: {
  blob: Blob;
  onClose: () => void;
  onSave: (prepared: { blob: Blob; sha256: string; size: number }) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const strokes = useRef<MarkupStroke[]>([]);
  const current = useRef<MarkupPoint[]>([]);
  const drawing = useRef(false);
  const [url, setUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [strokeCount, setStrokeCount] = useState(0);

  useEffect(() => {
    const next = URL.createObjectURL(blob);
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [blob]);

  useEffect(() => {
    if (!url || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const img = new Image();
    img.onload = () => {
      const maxW = Math.min(720, window.innerWidth - 32);
      const scale = Math.min(1, maxW / img.width);
      canvas.width = Math.max(1, Math.round(img.width * scale));
      canvas.height = Math.max(1, Math.round(img.height * scale));
      redraw(canvas, img, strokes.current);
    };
    img.src = url;
  }, [url]);

  const pointFromEvent = (e: React.PointerEvent<HTMLCanvasElement>): MarkupPoint => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    return {
      x: Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width)),
      y: Math.min(1, Math.max(0, (e.clientY - rect.top) / rect.height)),
    };
  };

  const onPointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    canvasRef.current?.setPointerCapture(e.pointerId);
    drawing.current = true;
    current.current = [pointFromEvent(e)];
  };
  const onPointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current || !canvasRef.current || !url) return;
    current.current.push(pointFromEvent(e));
    const img = new Image();
    img.onload = () => redraw(canvasRef.current!, img, [...strokes.current, { points: current.current }]);
    img.src = url;
  };
  const onPointerUp = () => {
    if (!drawing.current) return;
    drawing.current = false;
    if (current.current.length >= 2) {
      strokes.current = [...strokes.current, { points: current.current }];
      setStrokeCount(strokes.current.length);
    }
    current.current = [];
  };

  const clear = () => {
    strokes.current = [];
    setStrokeCount(0);
    setError(null);
    if (!url || !canvasRef.current) return;
    const img = new Image();
    img.onload = () => redraw(canvasRef.current!, img, []);
    img.src = url;
  };

  const save = () => {
    setBusy(true);
    setError(null);
    void bakeMarkupOntoPhoto(blob, strokes.current)
      .then((prepared) => onSave(prepared))
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Не удалось сохранить пометку'))
      .finally(() => setBusy(false));
  };

  return (
    <Dialog
      open
      onOpenChange={(o) => {
        if (!o && !busy) onClose();
      }}
      title="Пометка на фото"
      description="Обведите важное красным. Пометку можно сделать до отправки снимка на сервер."
      width="lg"
      footer={
        <div className="flex flex-wrap gap-2">
          <Button className="!h-11" variant="ghost" disabled={busy || !strokeCount} onClick={clear}>
            Очистить
          </Button>
          <Button className="!h-11" variant="ghost" disabled={busy} onClick={onClose}>
            Отмена
          </Button>
          <Button className="!h-11" disabled={busy || !strokeCount} onClick={save}>
            {busy ? 'Сохранение…' : 'Сохранить пометку'}
          </Button>
        </div>
      }
    >
      <div className="overflow-auto px-1 py-2">
        {error ? <p className="mb-2 text-sm text-danger">{error}</p> : null}
        <canvas
          ref={canvasRef}
          className="mx-auto block max-w-full touch-none rounded-md border border-line"
          style={{ touchAction: 'none' }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        />
      </div>
    </Dialog>
  );
}

function redraw(canvas: HTMLCanvasElement, img: HTMLImageElement, strokes: MarkupStroke[]) {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.strokeStyle = '#dc2626';
  ctx.lineWidth = Math.max(3, Math.round(Math.min(canvas.width, canvas.height) * 0.008));
  for (const stroke of strokes) {
    if (stroke.points.length < 2) continue;
    ctx.beginPath();
    stroke.points.forEach((p, i) => {
      const x = p.x * canvas.width;
      const y = p.y * canvas.height;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    ctx.stroke();
  }
}
